// src/pages/SettingsPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import LoadingSpinner from '../components/LoadingSpinner';
import TransactionSettings from '../components/TransactionSettings';

const SettingsPage = () => {
    const { loading, funMoneySettings, updateFunMoneySettings } = useAppContext();

    const [settings, setSettings] = useState(funMoneySettings || {
        monthly_allowance: 300,
        rollover_enabled: true,
        categories: [
            'Dining Out',
            'Entertainment',
            'Shopping',
            'Hobbies',
            'Other'
        ]
    });

    const [newCategory, setNewCategory] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    if (loading && !funMoneySettings) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner size="large" message="Loading settings..." />
            </div>
        );
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle different input types
        if (type === 'checkbox') {
            setSettings({
                ...settings,
                [name]: checked
            });
        } else if (type === 'number') {
            setSettings({
                ...settings,
                [name]: parseFloat(value)
            });
        } else {
            setSettings({
                ...settings,
                [name]: value
            });
        }
    };

    const handleAddCategory = () => {
        if (!newCategory.trim()) return;

        if (!settings.categories.includes(newCategory.trim())) {
            setSettings({
                ...settings,
                categories: [...settings.categories, newCategory.trim()]
            });
        }

        setNewCategory('');
    };

    const handleRemoveCategory = (category) => {
        setSettings({
            ...settings,
            categories: settings.categories.filter(cat => cat !== category)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setSubmitting(true);

        try {
            await updateFunMoneySettings(settings);
            setSuccess(true);
        } catch (err) {
            setError(err.message || 'Failed to update settings');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-3xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
                <Link
                    to="/"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                    Back to Dashboard
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Fun Money Settings</h2>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
                        Settings updated successfully!
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="monthly_allowance">
                            Monthly Allowance ($)
                        </label>
                        <input
                            type="number"
                            id="monthly_allowance"
                            name="monthly_allowance"
                            value={settings.monthly_allowance}
                            onChange={handleInputChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            min="0"
                            step="10"
                            required
                        />
                        <p className="text-sm text-gray-500 mt-1">
                            Amount each person gets monthly for discretionary spending
                        </p>
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="rollover_enabled"
                                name="rollover_enabled"
                                checked={settings.rollover_enabled}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-gray-700 text-sm font-bold" htmlFor="rollover_enabled">
                                Enable Balance Rollover
                            </label>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 ml-6">
                            If enabled, unused fun money will roll over to the next month
                        </p>
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                            Spending Categories
                        </label>

                        <div className="flex flex-wrap gap-2 mb-3">
                            {settings.categories.map((category) => (
                                <div key={category} className="bg-blue-100 px-3 py-1 rounded-full flex items-center">
                                    <span className="text-blue-800 mr-2">{category}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCategory(category)}
                                        className="text-blue-600 hover:text-blue-800"
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex">
                            <input
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                className="shadow appearance-none border rounded-l w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                placeholder="Add a new category"
                            />
                            <button
                                type="button"
                                onClick={handleAddCategory}
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:bg-gray-400"
                        >
                            {submitting ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <h2 className="text-xl font-semibold mb-4">About</h2>
                <p className="text-gray-600">
                    Financial Runway helps you track your savings and spending to ensure you have enough funds
                    for your medical school scholarship repayment when residency ends in June 2027.
                </p>
                <p className="text-gray-600 mt-3">
                    This app connects to your Monarch Money account to provide real-time updates on your financial position.
                </p>
                <p className="text-gray-600 mt-3">
                    Goal: Maintain at least $75,000 (ideally $100,000) by June 2027.
                </p>
            </div>

            {/* Transaction Settings Component */}
            <TransactionSettings />
        </div>
    );
};

export default SettingsPage;